import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';
import { Pencil, Eraser, Pipette, PaintBucket, Square, Box as BoxIcon } from 'lucide-react';
import { ReferencePlatform } from './ThreeViewer';

const MC_SCALE = 1 / 16;
const DEFAULT_RESOLUTION = { width: 64, height: 64 };
// Color base opaco para cubos sin pintar (gris claro Blockbench-like)
const BASE_FILL = '#B0B0B0';

/**
 * Convierte hex a rgba (alpha 0-255)
 */
const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
};

/**
 * Face map: Three.js BoxGeometry side order
 * BoxGeometry indices per side (2 triangles each):
 *   0,1 = +X (east), 2,3 = -X (west), 4,5 = +Y (up),
 *   6,7 = -Y (down), 8,9 = +Z (south), 10,11 = -Z (north)
 */
const FACE_MAP = ['east', 'west', 'up', 'down', 'south', 'north'];

/**
 * Genera geometría BoxGeometry con UVs personalizadas según cube.faces
 */
const buildGeometry = (scaledSize, faces, canvasTexture) => {
  const geom = new THREE.BoxGeometry(scaledSize[0], scaledSize[1], scaledSize[2]);
  if (!faces || !canvasTexture) return geom;

  const uvAttr = geom.attributes.uv;
  const tw = canvasTexture.image.width;
  const th = canvasTexture.image.height;

  FACE_MAP.forEach((faceName, faceIdx) => {
    const face = faces[faceName];
    if (!face || !face.uv) return;
    const [u1, v1, u2, v2] = face.uv;
    // Normalizar a 0-1 en el atlas
    const nu1 = u1 / tw;
    const nu2 = u2 / tw;
    // V está invertida (Blockbench top-down, three.js bottom-up)
    const nv1 = 1 - v1 / th;
    const nv2 = 1 - v2 / th;

    const startIdx = faceIdx * 4;
    uvAttr.setXY(startIdx + 0, nu1, nv1);
    uvAttr.setXY(startIdx + 1, nu2, nv1);
    uvAttr.setXY(startIdx + 2, nu1, nv2);
    uvAttr.setXY(startIdx + 3, nu2, nv2);
  });
  uvAttr.needsUpdate = true;
  return geom;
};

/**
 * Cubo pintable con detección de cara + coord de píxel exacta
 */
const PaintableCube = ({
  cube,
  canvasTexture,
  onPaintFace,
  onPickColor,
  currentTool,
  isDown,
  onPaintStart,
}) => {
  const meshRef = useRef();

  const centerPos = useMemo(() => [
    ((cube.from[0] + cube.to[0]) / 2) * MC_SCALE,
    ((cube.from[1] + cube.to[1]) / 2) * MC_SCALE,
    ((cube.from[2] + cube.to[2]) / 2) * MC_SCALE,
  ], [cube.from, cube.to]);

  const scaledSize = useMemo(() => [
    cube.size[0] * MC_SCALE,
    cube.size[1] * MC_SCALE,
    cube.size[2] * MC_SCALE,
  ], [cube.size]);

  const geometry = useMemo(
    () => buildGeometry(scaledSize, cube.faces, canvasTexture),
    [scaledSize, cube.faces, canvasTexture]
  );

  // MeshBasicMaterial (sin iluminación) para que el color que ve el usuario
  // en el pincel/canvas sea EXACTAMENTE el que se aplica al modelo 3D.
  // MeshStandardMaterial multiplica por luces → desatura el color percibido.
  const material = useMemo(() => (
    new THREE.MeshBasicMaterial({
      map: canvasTexture,
      transparent: true,
      alphaTest: 0.02,
      side: THREE.DoubleSide,
    })
  ), [canvasTexture]);

  // Material shader que dibuja una cuadrícula fina píxel por píxel sobre el modelo
  const pixelGridMaterial = useMemo(() => {
    if (!canvasTexture) return null;
    const tw = canvasTexture.image.width;
    const th = canvasTexture.image.height;
    return new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      uniforms: {
        texSize: { value: new THREE.Vector2(tw, th) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec2 texSize;
        varying vec2 vUv;
        void main() {
          vec2 pxUv = vUv * texSize;
          vec2 f = fract(pxUv);
          float edge = 0.0;
          // Línea sutil en el borde del píxel (0.02 = 2% del píxel)
          if (f.x < 0.06 || f.x > 0.94 || f.y < 0.06 || f.y > 0.94) edge = 1.0;
          if (edge < 0.5) discard;
          gl_FragColor = vec4(0.29, 0.62, 1.0, 0.22);
        }
      `,
    });
  }, [canvasTexture]);

  const handleEvent = useCallback((e) => {
    e.stopPropagation();
    if (!canvasTexture || !cube.faces) return;
    const uv = e.uv;
    if (!uv) return;
    const tw = canvasTexture.image.width;
    const th = canvasTexture.image.height;
    const texX = Math.floor(uv.x * tw);
    const texY = Math.floor((1 - uv.y) * th);
    if (texX < 0 || texY < 0 || texX >= tw || texY >= th) return;

    const faceIdx = Math.floor(e.faceIndex / 2);
    const faceName = FACE_MAP[faceIdx];

    if (currentTool === 'eyedropper') {
      onPickColor(texX, texY);
      return;
    }
    onPaintFace(cube, faceName, texX, texY);
  }, [canvasTexture, cube, currentTool, onPaintFace, onPickColor]);

  return (
    <group>
      <mesh
        ref={meshRef}
        position={centerPos}
        rotation={cube.rotation || [0, 0, 0]}
        geometry={geometry}
        material={material}
        onPointerDown={(e) => {
          onPaintStart && onPaintStart();
          handleEvent(e);
        }}
        onPointerMove={(e) => {
          if (isDown && currentTool !== 'eyedropper' && currentTool !== 'bucket') {
            handleEvent(e);
          }
        }}
      />
      {/* Cuadrícula fina de píxeles overlay */}
      {pixelGridMaterial && (
        <mesh
          position={centerPos}
          rotation={cube.rotation || [0, 0, 0]}
          geometry={geometry}
          material={pixelGridMaterial}
          raycast={() => null}
        />
      )}
    </group>
  );
};

/**
 * Renderizador del modelo pintable con offset centrado
 */
const PaintableModel = ({ model, canvasTexture, onPaintFace, onPickColor, currentTool, isDown, onPaintStart }) => {
  const bbox = useMemo(() => {
    if (!model?.cubes || model.cubes.length === 0) {
      return { minY: 0, center: [0, 0, 0] };
    }
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxZ = -Infinity;
    model.cubes.forEach((cube) => {
      minX = Math.min(minX, cube.from[0]);
      minY = Math.min(minY, cube.from[1]);
      minZ = Math.min(minZ, cube.from[2]);
      maxX = Math.max(maxX, cube.to[0]);
      maxZ = Math.max(maxZ, cube.to[2]);
    });
    return { minY, center: [(minX + maxX) / 2, minY, (minZ + maxZ) / 2] };
  }, [model?.cubes]);

  if (!model?.cubes) return null;

  const offsetX = -bbox.center[0] * MC_SCALE;
  const offsetY = -bbox.minY * MC_SCALE;
  const offsetZ = -bbox.center[2] * MC_SCALE;

  return (
    <group position={[offsetX, offsetY, offsetZ]}>
      {model.cubes.filter((c) => c.visible !== false).map((cube) => (
        <PaintableCube
          key={cube.id}
          cube={cube}
          canvasTexture={canvasTexture}
          onPaintFace={onPaintFace}
          onPickColor={onPickColor}
          currentTool={currentTool}
          isDown={isDown}
          onPaintStart={onPaintStart}
        />
      ))}
    </group>
  );
};

/**
 * ModelPainter - Editor de texturas pixel-perfect
 * - Pintado directo sobre el modelo 3D respetando las UVs de Blockbench
 * - Modos: cara única, todo el cubo
 * - Opacidad ajustable
 * - Cuentagotas, borrador, cubo pintura
 */
const ModelPainter = ({ model, onUpdate }) => {
  const canvasRef = useRef(null);
  const [tool, setTool] = useState('pencil');
  const [color, setColor] = useState('#ff5555');
  const [bucketMode, setBucketMode] = useState('face'); // Solo aplica al bucket: 'face' | 'element'
  const [brushSize, setBrushSize] = useState(1); // 1, 2, 4, 8 - aplica a pencil y eraser
  const [opacity, setOpacity] = useState(100);
  const [isDown, setIsDown] = useState(false);
  const [canvasTexture, setCanvasTexture] = useState(null);
  const [recentColors, setRecentColors] = useState([
    '#ff5555', '#55ff55', '#5555ff', '#ffff55',
    '#ff55ff', '#55ffff', '#ffffff', '#000000',
    '#8b4513', '#7d7d7d', '#f5deb3', '#ffcc99',
  ]);
  const [previewVersion, setPreviewVersion] = useState(0);

  const resolution = model?.resolution || DEFAULT_RESOLUTION;

  // Inicialización del canvas + texture
  useEffect(() => {
    const canvas = document.createElement('canvas');
    canvas.width = resolution.width;
    canvas.height = resolution.height;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    canvasRef.current = canvas;

    const createTexture = () => {
      const tex = new THREE.CanvasTexture(canvas);
      tex.magFilter = THREE.NearestFilter;
      tex.minFilter = THREE.NearestFilter;
      tex.wrapS = THREE.ClampToEdgeWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.flipY = true;
      // Marcar la textura como sRGB para que Three.js NO aplique
      // conversión de linear→sRGB (causa del efecto "desaturado")
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.needsUpdate = true;
      setCanvasTexture(tex);
    };

    if (model?.texture) {
      // Preservar TRANSPARENCIA original del modelo importado
      // NO rellenar con color base — el canvas empieza transparente
      ctx.clearRect(0, 0, resolution.width, resolution.height);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        ctx.clearRect(0, 0, resolution.width, resolution.height);
        ctx.drawImage(img, 0, 0, resolution.width, resolution.height);
        createTexture();
      };
      img.onerror = () => createTexture();
      img.src = model.texture;
    } else {
      // Modelo sin textura previa → llenar con color base opaco visible
      ctx.fillStyle = BASE_FILL;
      ctx.fillRect(0, 0, resolution.width, resolution.height);
      createTexture();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model?.texture, resolution.width, resolution.height]);

  const addRecentColor = useCallback((col) => {
    setRecentColors((prev) => {
      const filtered = prev.filter((c) => c.toLowerCase() !== col.toLowerCase());
      return [col, ...filtered].slice(0, 12);
    });
  }, []);

  const saveTimerRef = useRef(null);
  const scheduleSaveTexture = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      if (canvasRef.current) {
        const dataUrl = canvasRef.current.toDataURL('image/png');
        onUpdate({ ...model, texture: dataUrl });
      }
    }, 500);
  }, [model, onUpdate]);

  // Pinta un rect [x1,y1,x2,y2] con el color y opacidad
  // El borrador SIEMPRE limpia el pixel a totalmente transparente (respetando el diseño real de Minecraft)
  const paintRect = useCallback((ctx, x1, y1, x2, y2, hexColor, alpha01, eraser = false) => {
    const xMin = Math.min(x1, x2);
    const yMin = Math.min(y1, y2);
    const xMax = Math.max(x1, x2);
    const yMax = Math.max(y1, y2);
    if (eraser) {
      // Borrado real: totalmente transparente (para respetar zonas transparentes)
      ctx.clearRect(xMin, yMin, xMax - xMin, yMax - yMin);
    } else {
      ctx.fillStyle = hexColor;
      ctx.globalAlpha = alpha01;
      ctx.fillRect(xMin, yMin, xMax - xMin, yMax - yMin);
      ctx.globalAlpha = 1;
    }
  }, []);

  // Pintar un solo píxel
  const paintPixel = useCallback((ctx, x, y, hexColor, alpha01, eraser = false) => {
    paintRect(ctx, x, y, x + 1, y + 1, hexColor, alpha01, eraser);
  }, [paintRect]);

  // Callback cuando se pinta sobre una cara
  const handlePaintFace = useCallback((cube, faceName, texX, texY) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const alpha01 = opacity / 100;
    const isEraser = tool === 'eraser';

    if (tool === 'bucket') {
      if (bucketMode === 'element') {
        // Pintar TODAS las 6 caras del cubo con el color (rellenando cada UV)
        FACE_MAP.forEach((fn) => {
          const face = cube.faces?.[fn];
          if (!face) return;
          const [u1, v1, u2, v2] = face.uv;
          paintRect(ctx, u1, v1, u2, v2, color, alpha01, false);
        });
      } else {
        // Modo 'face': pintar la cara ENTERA que se hizo clic, con el color
        // (rellena todos los píxeles de esa cara, ignorando el color previo)
        const face = cube.faces?.[faceName];
        if (face) {
          const [u1, v1, u2, v2] = face.uv;
          paintRect(ctx, u1, v1, u2, v2, color, alpha01, false);
        }
      }
    } else if (tool === 'pencil' || tool === 'eraser') {
      // Pincel/goma con tamaño 1x1, 2x2, 4x4, 8x8
      const s = brushSize;
      const half = Math.floor(s / 2);
      const x = texX - half;
      const y = texY - half;
      paintRect(ctx, x, y, x + s, y + s, color, alpha01, isEraser);
    }

    if (canvasTexture) canvasTexture.needsUpdate = true;
    setPreviewVersion((v) => v + 1);
    scheduleSaveTexture();
  }, [tool, bucketMode, brushSize, opacity, color, canvasTexture, scheduleSaveTexture, paintRect]);

  const handlePickColor = useCallback((x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const data = ctx.getImageData(x, y, 1, 1).data;
    const picked = `#${[data[0], data[1], data[2]].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
    setColor(picked);
    addRecentColor(picked);
    setTool('pencil');
  }, [addRecentColor]);

  const floodFill = (ctx, startX, startY, fillColor, alpha01) => {
    const w = resolution.width;
    const h = resolution.height;
    const imgData = ctx.getImageData(0, 0, w, h);
    const data = imgData.data;
    const idx = (x, y) => (y * w + x) * 4;

    const startIdx = idx(startX, startY);
    const tR = data[startIdx], tG = data[startIdx + 1], tB = data[startIdx + 2], tA = data[startIdx + 3];
    const { r, g, b } = hexToRgb(fillColor);
    const finalA = Math.round(alpha01 * 255);
    if (tR === r && tG === g && tB === b && tA === finalA) return;

    const stack = [[startX, startY]];
    while (stack.length > 0) {
      const [x, y] = stack.pop();
      if (x < 0 || y < 0 || x >= w || y >= h) continue;
      const i = idx(x, y);
      if (data[i] !== tR || data[i + 1] !== tG || data[i + 2] !== tB || data[i + 3] !== tA) continue;
      // Blend con opacidad
      data[i] = Math.round(r * alpha01 + tR * (1 - alpha01));
      data[i + 1] = Math.round(g * alpha01 + tG * (1 - alpha01));
      data[i + 2] = Math.round(b * alpha01 + tB * (1 - alpha01));
      data[i + 3] = 255;
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
    ctx.putImageData(imgData, 0, 0);
  };

  const TOOLS = [
    { id: 'pencil', icon: Pencil, label: 'Lápiz', color: 'bg-blue-600' },
    { id: 'eraser', icon: Eraser, label: 'Borrador', color: 'bg-red-500' },
    { id: 'bucket', icon: PaintBucket, label: 'Rellenar', color: 'bg-purple-500' },
    { id: 'eyedropper', icon: Pipette, label: 'Cuentagotas', color: 'bg-amber-500' },
  ];

  const PAINT_MODES = [
    { id: 'face', icon: Square, label: 'Cara', help: 'Rellena solo el área continua de la cara' },
    { id: 'element', icon: BoxIcon, label: 'Elemento', help: 'Rellena las 6 caras del cubo' },
  ];

  const BRUSH_SIZES = [1, 2, 4, 8];
  const showBrushSize = tool === 'pencil' || tool === 'eraser';
  const showBucketMode = tool === 'bucket';

  const cursorClass = 'cursor-crosshair';

  return (
    <div className="flex flex-1 h-full">
      {/* Toolbar izquierda */}
      <div className="w-14 bg-gray-900 flex flex-col items-center py-3 gap-1 flex-shrink-0">
        {TOOLS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTool(t.id)}
              title={t.label}
              className={`w-10 h-10 rounded flex items-center justify-center transition-colors ${
                tool === t.id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}
              data-testid={`paint-tool-${t.id}`}
            >
              <Icon className="w-4 h-4" />
            </button>
          );
        })}
      </div>

      {/* Canvas 3D central — mismos controles de cámara que el modelador.
          El drag paint sólo se activa cuando el pointerDown IMPACTA en el modelo.
          Al arrastrar en zonas vacías, OrbitControls hace su trabajo normal. */}
      <div
        className={`flex-1 relative ${cursorClass}`}
        style={{ background: '#1E1E23' }}
        onPointerUp={() => setIsDown(false)}
        onPointerLeave={() => setIsDown(false)}
      >
        <Canvas gl={{ antialias: true, alpha: false }}>
          <PerspectiveCamera makeDefault position={[2.5, 2, 2.5]} fov={45} />
          <ambientLight intensity={0.9} />
          <directionalLight position={[5, 10, 5]} intensity={0.5} />
          <directionalLight position={[-5, 5, -5]} intensity={0.3} />
          <directionalLight position={[0, -5, 3]} intensity={0.2} />

          <ReferencePlatform />

          {canvasTexture && (
            <PaintableModel
              model={model}
              canvasTexture={canvasTexture}
              onPaintFace={handlePaintFace}
              onPickColor={handlePickColor}
              currentTool={tool}
              isDown={isDown}
              onPaintStart={() => setIsDown(true)}
            />
          )}

          <OrbitControls
            enablePan
            enableZoom
            enableRotate
            minDistance={0.4}
            maxDistance={10}
            target={[0, 0.5, 0]}
            panSpeed={0.8}
            zoomSpeed={1.2}
            rotateSpeed={0.8}
            enabled={!isDown}
          />
        </Canvas>

        {/* Overlay info */}
        <div className="absolute top-3 left-3 bg-gray-900/80 backdrop-blur text-xs text-gray-300 px-2.5 py-1.5 rounded font-mono pointer-events-none">
          Pinta directamente sobre el modelo
        </div>

        <div className="absolute top-3 right-3 bg-blue-600 text-white text-xs px-2.5 py-1.5 rounded pointer-events-none flex items-center gap-2">
          {(() => {
            const activeTool = TOOLS.find((t) => t.id === tool);
            const Icon = activeTool?.icon || Pencil;
            return <><Icon className="w-3.5 h-3.5" /> {activeTool?.label}</>;
          })()}
        </div>

        <div className="absolute bottom-3 left-3 bg-gray-900/70 backdrop-blur text-[10px] text-gray-400 px-2 py-1 rounded font-mono">
          Clic izquierdo pinta · Arrastra en el fondo para girar
        </div>
      </div>

      {/* Sidebar derecha */}
      <div className="w-64 bg-white border-l border-gray-200 overflow-y-auto flex-shrink-0">
        <div className="p-4 space-y-5">
          {/* Color selector */}
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-2 block uppercase tracking-wide">
              Color actual
            </label>
            <div className="flex items-center gap-2">
              <div
                className="w-12 h-12 rounded border-2 border-gray-200 flex-shrink-0"
                style={{ backgroundColor: color }}
              />
              <div className="flex-1 space-y-1">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-full h-6 cursor-pointer border border-gray-200 rounded"
                  data-testid="color-picker"
                />
                <input
                  type="text"
                  value={color.toUpperCase()}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setColor(v);
                  }}
                  onBlur={() => addRecentColor(color)}
                  className="w-full h-6 border border-gray-200 rounded px-2 text-xs font-mono"
                />
              </div>
            </div>
          </div>

          {/* Recent colors */}
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-2 block uppercase tracking-wide">
              Recientes
            </label>
            <div className="grid grid-cols-6 gap-1">
              {recentColors.map((c, i) => (
                <button
                  key={i}
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded border-2 transition-all ${color.toLowerCase() === c.toLowerCase() ? 'border-blue-500 scale-110' : 'border-gray-200 hover:scale-105'}`}
                  style={{ backgroundColor: c }}
                  title={c}
                />
              ))}
            </div>
          </div>

          {/* Tamaño del pincel (solo para pencil y eraser) */}
          {showBrushSize && (
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-2 block uppercase tracking-wide">
                Tamaño del {tool === 'pencil' ? 'pincel' : 'borrador'}
              </label>
              <div className="grid grid-cols-4 gap-1">
                {BRUSH_SIZES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setBrushSize(s)}
                    className={`h-9 rounded border-2 text-xs font-medium transition-colors ${
                      brushSize === s
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-blue-500'
                    }`}
                    data-testid={`brush-size-${s}`}
                  >
                    {s}×{s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Modo de bucket (solo para bucket) */}
          {showBucketMode && (
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-2 block uppercase tracking-wide">
                Modo del cubo de pintura
              </label>
              <div className="grid grid-cols-2 gap-1.5">
                {PAINT_MODES.map((m) => {
                  const Icon = m.icon;
                  const active = bucketMode === m.id;
                  return (
                    <button
                      key={m.id}
                      onClick={() => setBucketMode(m.id)}
                      title={m.help}
                      className={`h-14 rounded border-2 flex flex-col items-center justify-center gap-1 text-xs font-medium transition-colors ${
                        active
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-blue-500'
                      }`}
                      data-testid={`bucket-mode-${m.id}`}
                    >
                      <Icon className="w-4 h-4" />
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Opacity */}
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-2 block uppercase tracking-wide">
              Opacidad
              <span className="ml-2 text-gray-400 font-mono normal-case">{opacity}%</span>
            </label>
            <input
              type="range"
              min={1}
              max={100}
              step={1}
              value={opacity}
              onChange={(e) => setOpacity(parseInt(e.target.value, 10))}
              className="w-full accent-blue-600"
              data-testid="opacity-slider"
            />
          </div>

          {/* Preview textura */}
          <div className="pt-4 border-t border-gray-200">
            <label className="text-xs font-semibold text-gray-700 mb-2 block uppercase tracking-wide">
              Vista de la textura
            </label>
            <div
              className="border border-gray-200 rounded p-2 flex items-center justify-center overflow-hidden"
              style={{ background: '#2E2E34' }}
            >
              {canvasTexture && (
                <img
                  key={previewVersion}
                  src={canvasRef.current?.toDataURL()}
                  alt="Textura"
                  className="max-w-full"
                  style={{
                    imageRendering: 'pixelated',
                    width: '160px',
                    height: '160px',
                    objectFit: 'contain',
                  }}
                />
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1 font-mono text-center">
              {resolution.width}×{resolution.height}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelPainter;
